import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const Header = ({ onBookClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== "/") {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { label: "Home", action: () => scrollToSection("hero") },
    { label: "Services", action: () => scrollToSection("services") },
    { label: "About", action: () => scrollToSection("why-choose") },
    { label: "Contact", action: () => scrollToSection("contact") },
  ];

  return (
    <>
      <header
        data-testid="sticky-header"
        className={`sticky-header ${isScrolled ? "glass shadow-lg" : "bg-white/95"}`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2"
              data-testid="logo-link"
            >
              <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900 font-outfit">
                Nova Shelf
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={link.action}
                  className="nav-link"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href="tel:+18884532345"
                className="flex items-center gap-2 text-slate-700 hover:text-sky-600 transition-colors"
                data-testid="phone-link"
              >
                <Phone className="w-5 h-5" />
                <span className="font-medium">(888) 453-2345</span>
              </a>
              <Button
                onClick={onBookClick}
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 py-2 font-semibold shadow-md hover:shadow-lg transition-all duration-300"
                data-testid="book-cleaning-btn"
              >
                Book Cleaning Service
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-900" />
              ) : (
                <Menu className="w-6 h-6 text-slate-900" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`mobile-menu ${isMobileMenuOpen ? "open" : "closed"}`}
        data-testid="mobile-menu"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">Nova Shelf</span>
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="mobile-menu-close"
            >
              <X className="w-6 h-6 text-slate-900" />
            </button>
          </div>

          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={link.action}
                className="text-left text-lg font-medium text-slate-700 py-3 border-b border-slate-100"
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="mt-8 space-y-4">
            <a
              href="tel:+18884532345"
              className="flex items-center justify-center gap-2 w-full py-4 bg-slate-100 rounded-xl text-slate-900 font-medium"
              data-testid="mobile-phone-link"
            >
              <Phone className="w-5 h-5" />
              (888) 453-2345
            </a>
            <Button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onBookClick();
              }}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-4 font-semibold"
              data-testid="mobile-book-btn"
            >
              Book Cleaning Service
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
